import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatMessageRow, fetchMessages, sendMessage } from '../services/chat';
import { ApiError } from '../services/types';

/**
 * One booking's message thread, from the platform.
 *
 * ---------------------------------------------------------------------------
 * POLLED, NOT PUSHED
 * ---------------------------------------------------------------------------
 * The backend has a live Socket.IO gateway for tracking but not yet one for
 * chat - `GET /bookings/:id/messages` is the only way to learn about a
 * message someone else sent. So this hook polls it every `POLL_MS` while a
 * thread is open, and stops the moment it is not - a moving conversation
 * that only refreshes when foregrounded is honest; pretending otherwise
 * would mean claiming a push channel that does not exist.
 *
 * When a chat gateway exists, this is the one place that changes: swap the
 * interval for a socket subscription and everything downstream - the
 * screen, the message shape - stays the same.
 */
const POLL_MS = 4000;

export type UseChat = {
  messages: ChatMessageRow[];
  /** First load, nothing on screen yet. */
  loading: boolean;
  /** Set when the last read or send failed. Cleared on the next success. */
  error: string | null;
  /** True once the write window has closed - a 409 on send. Hides the composer. */
  closed: boolean;
  sending: boolean;
  send: (body: string) => Promise<void>;
  reload: () => void;
};

export function useChat(bookingId: string | undefined): UseChat {
  const [messages, setMessages] = useState<ChatMessageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [closed, setClosed] = useState(false);
  const [sending, setSending] = useState(false);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!bookingId) {
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const rows = await fetchMessages(bookingId);
        if (!alive.current) {
          return;
        }
        setMessages(rows);
        setError(null);
      } catch (failure) {
        // A poll tick that fails is not worth interrupting an open thread
        // for - only the first, blocking load surfaces the error.
        if (alive.current && !opts?.silent) {
          setError(
            failure instanceof Error
              ? failure.message
              : 'Could not load this conversation.',
          );
        }
      } finally {
        if (alive.current) {
          setLoading(false);
        }
      }
    },
    [bookingId],
  );

  useEffect(() => {
    setMessages([]);
    setError(null);
    setClosed(false);
    if (!bookingId) {
      setLoading(false);
      return;
    }

    load();
    const timer = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [bookingId, load]);

  const send = useCallback<UseChat['send']>(
    async body => {
      const trimmed = body.trim();
      if (!bookingId || !trimmed) {
        return;
      }
      setSending(true);
      try {
        const created = await sendMessage(bookingId, trimmed);
        if (alive.current) {
          setMessages(current => [...current, created]);
          setError(null);
        }
      } catch (failure) {
        if (failure instanceof ApiError && failure.status === 409) {
          if (alive.current) {
            setClosed(true);
          }
          return;
        }
        throw failure;
      } finally {
        if (alive.current) {
          setSending(false);
        }
      }
    },
    [bookingId],
  );

  return {
    messages,
    loading,
    error,
    closed,
    sending,
    send,
    reload: () => load(),
  };
}

export default useChat;
